import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Chip,
  IconButton,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  ExpandMore,
  Settings,
  Visibility,
  Add,
  Remove,
  Edit,
  AlignHorizontalLeft,
  AlignHorizontalCenter,
  AlignHorizontalRight,
  AlignVerticalTop,
  AlignVerticalCenter,
  AlignVerticalBottom,
  FormatItalic,
  FormatUnderlined,
  FormatStrikethrough,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
} from '@mui/icons-material';
import { isAdminRole } from '../../../utils/userRoles';





// Hook для дебаунсинга
const useDebounce = (callback, delay) => {
  const timeoutRef = useRef(null);
  
  return useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);
};

// Google Fonts список
const googleFonts = [
  { name: 'Arial', value: 'Arial, sans-serif', category: 'Sans Serif' },
  { name: 'Helvetica', value: 'Helvetica, Arial, sans-serif', category: 'Sans Serif' },
  { name: 'Times New Roman', value: 'Times New Roman, serif', category: 'Serif' },
  { name: 'Georgia', value: 'Georgia, serif', category: 'Serif' },
  { name: 'Courier New', value: 'Courier New, monospace', category: 'Monospace' },
  { name: 'Roboto', value: 'Roboto, sans-serif', category: 'Google Fonts', googleFont: true },
  { name: 'Open Sans', value: 'Open Sans, sans-serif', category: 'Google Fonts', googleFont: true },
  { name: 'Lato', value: 'Lato, sans-serif', category: 'Google Fonts', googleFont: true },
  { name: 'Montserrat', value: 'Montserrat, sans-serif', category: 'Google Fonts', googleFont: true },
  { name: 'Source Sans Pro', value: 'Source Sans Pro, sans-serif', category: 'Google Fonts', googleFont: true },
  { name: 'Oswald', value: 'Oswald, sans-serif', category: 'Google Fonts', googleFont: true },
  { name: 'Raleway', value: 'Raleway, sans-serif', category: 'Google Fonts', googleFont: true },
  { name: 'PT Sans', value: 'PT Sans, sans-serif', category: 'Google Fonts', googleFont: true },
  { name: 'Ubuntu', value: 'Ubuntu, sans-serif', category: 'Google Fonts', googleFont: true },
  { name: 'Nunito', value: 'Nunito, sans-serif', category: 'Google Fonts', googleFont: true },
  { name: 'Playfair Display', value: 'Playfair Display, serif', category: 'Google Fonts', googleFont: true },
  { name: 'Merriweather', value: 'Merriweather, serif', category: 'Google Fonts', googleFont: true },
  { name: 'Lora', value: 'Lora, serif', category: 'Google Fonts', googleFont: true },
  { name: 'Crimson Text', value: 'Crimson Text, serif', category: 'Google Fonts', googleFont: true },
  { name: 'Poppins', value: 'Poppins, sans-serif', category: 'Google Fonts', googleFont: true },
  { name: 'Inter', value: 'Inter, sans-serif', category: 'Google Fonts', googleFont: true },
  { name: 'Fira Sans', value: 'Fira Sans, sans-serif', category: 'Google Fonts', googleFont: true },
  { name: 'Work Sans', value: 'Work Sans, sans-serif', category: 'Google Fonts', googleFont: true },
  { name: 'Dancing Script', value: 'Dancing Script, cursive', category: 'Google Fonts', googleFont: true },
  { name: 'Pacifico', value: 'Pacifico, cursive', category: 'Google Fonts', googleFont: true },
];

// Функция для загрузки Google Fonts
const loadGoogleFont = (fontValue) => {
  if (!fontValue) return;

  const fontObj = googleFonts.find((f) => f.value === fontValue);
  if (!fontObj?.googleFont) return;

  const fontFamily = fontValue.split(',')[0].replace(/'/g, '').trim();
  const href = `https://fonts.googleapis.com/css2?family=${fontFamily.replace(/ /g, '+')}:wght@300;400;500;600;700&display=swap`;

  if (!document.querySelector(`link[href="${href}"]`)) {
    const link = document.createElement('link');
    link.href = href;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }
};

const PropertyPanel = ({
  selectedBlock,
  templateSettings,
  user,
  onUpdateBlock,
  onUpdateSettings,
}) => {
  const [expandedSection, setExpandedSection] = useState('content');
  const [localValues, setLocalValues] = useState({});

  // Синхронизируем локальные значения с выбранным блоком
  useEffect(() => {
    if (selectedBlock) {
      setLocalValues(selectedBlock.data || {});
    } else {
      setLocalValues({});
    }
  }, [selectedBlock]);
  
  // БЫСТРАЯ функция для применения обновлений (объявляем ПОСЛЕ updateNestedBlock!)
  const applyUpdate = useCallback((property, value) => {
    if (!selectedBlock) return;

    if (
      selectedBlock.type === 'unsubscribe' &&
      !isAdminRole(user?.role)
    ) {
      return;
    }

    // Обычный верхнеуровневый блок - прямое обновление без лишних операций
    if (onUpdateBlock) {
      onUpdateBlock(selectedBlock.id, (prevData) => {
        if (!prevData) prevData = {};
        return { ...prevData, [property]: value };
      });
    }
  }, [selectedBlock, onUpdateBlock, user]);

  // Дебаунсированная функция для глобального обновления
  const debouncedApplyUpdate = useDebounce(applyUpdate, 150);

  // МГНОВЕННАЯ функция для локального обновления UI + дебаунс для глобального
  const handleQuickUpdate = useCallback((property, value, immediate = false) => {
    // 1. МГНОВЕННО обновляем локальное значение для UI
    setLocalValues(prev => ({
      ...prev,
      [property]: value
    }));
    
    // 2. Для слайдеров и селектов - обновляем сразу
    if (immediate || typeof value === 'number' || typeof value === 'boolean') {
      applyUpdate(property, value);
    } else {
      // 3. Для текстовых полей - используем дебаунс
      debouncedApplyUpdate(property, value);
    }
  }, [applyUpdate, debouncedApplyUpdate]);

  const handleSettingsUpdate = (property, value) => {
    if (onUpdateSettings) {
      onUpdateSettings({
        ...templateSettings,
        [property]: value,
      });
    }
  };

  const renderColorPicker = (label, property) => {
    const value = localValues[property] || '#ffffff';

    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            value={value}
            onChange={(e) => handleQuickUpdate(property, e.target.value)}
            placeholder="#ffffff"
            sx={{ flex: 1 }}
          />
          {/* Цветовой кубик с встроенным color input */}
          <Box
            sx={{
              width: 40,
              height: 32,
              backgroundColor: value,
              border: '1px solid #ddd',
              borderRadius: 1,
              cursor: 'pointer',
              position: 'relative',
              flexShrink: 0,
            }}
          >
            <input
              type="color"
              value={value}
              onChange={(e) => handleQuickUpdate(property, e.target.value, true)}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                opacity: 0,
                cursor: 'pointer',
                border: 'none',
              }}
            />
          </Box>
        </Box>
      </Box>
    );
  };

  const renderSlider = (label, property, min = 0, max = 100, step = 1, unit = '') => {
    const currentValue = localValues[property];
    const displayValue = currentValue !== undefined ? currentValue : (min + max) / 2;
    
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
          <span>{label}</span>
          <span style={{ fontWeight: 'bold', color: '#007bff' }}>{displayValue}{unit}</span>
        </Typography>
        <Slider
          value={displayValue}
          onChange={(e, newValue) => handleQuickUpdate(property, newValue)}
          min={min}
          max={max}
          step={step}
          size="small"
          sx={{
            color: '#007bff',
            '& .MuiSlider-thumb': {
              width: 16,
              height: 16,
            },
          }}
        />
      </Box>
    );
  };

  // Новые функции для иконочных контролов
  const renderFontWeightControl = () => {
    const fontWeight = localValues.fontWeight || 'normal';
    
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Font Weight
        </Typography>
        <ToggleButtonGroup
          value={fontWeight}
          exclusive
          onChange={(e, newWeight) => {
            if (newWeight !== null) {
              handleQuickUpdate('fontWeight', newWeight);
            }
          }}
          sx={{ display: 'flex', width: '100%' }}
        >
          <ToggleButton value="300" sx={{ flex: 1, fontSize: '12px' }}>
            Light
          </ToggleButton>
          <ToggleButton value="normal" sx={{ flex: 1, fontSize: '12px' }}>
            Normal
          </ToggleButton>
          <ToggleButton value="bold" sx={{ flex: 1, fontSize: '12px' }}>
            Bold
          </ToggleButton>
          <ToggleButton value="900" sx={{ flex: 1, fontSize: '12px' }}>
            Extra
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
    );
  };

  const renderTextStyleControls = () => {
    const fontStyle = localValues.fontStyle || 'normal';
    const textDecoration = localValues.textDecoration || 'none';
    
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Text Style
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <ToggleButton
            value="italic"
            selected={fontStyle === 'italic'}
            onChange={() => {
              handleQuickUpdate('fontStyle', fontStyle === 'italic' ? 'normal' : 'italic');
            }}
            sx={{ flex: 1 }}
          >
            <FormatItalic />
          </ToggleButton>
          <ToggleButton
            value="underline"
            selected={textDecoration === 'underline'}
            onChange={() => {
              handleQuickUpdate('textDecoration', textDecoration === 'underline' ? 'none' : 'underline');
            }}
            sx={{ flex: 1 }}
          >
            <FormatUnderlined />
          </ToggleButton>
          <ToggleButton
            value="line-through"
            selected={textDecoration === 'line-through'}
            onChange={() => {
              handleQuickUpdate('textDecoration', textDecoration === 'line-through' ? 'none' : 'line-through');
            }}
            sx={{ flex: 1 }}
          >
            <FormatStrikethrough />
          </ToggleButton>
        </Box>
      </Box>
    );
  };

  const renderTextAlignmentControl = () => {
    const textAlign = localValues.textAlign || 'left';
    
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Text Alignment
        </Typography>
        <ToggleButtonGroup
          value={textAlign}
          exclusive
          onChange={(e, newAlign) => {
            if (newAlign !== null) {
              handleQuickUpdate('textAlign', newAlign);
            }
          }}
          sx={{ display: 'flex', width: '100%' }}
        >
          <ToggleButton value="left" sx={{ flex: 1 }}>
            <FormatAlignLeft />
          </ToggleButton>
          <ToggleButton value="center" sx={{ flex: 1 }}>
            <FormatAlignCenter />
          </ToggleButton>
          <ToggleButton value="right" sx={{ flex: 1 }}>
            <FormatAlignRight />
          </ToggleButton>
          <ToggleButton value="justify" sx={{ flex: 1 }}>
            <FormatAlignJustify />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
    );
  };

  const renderFontFamilyControl = () => {
    const currentFont = localValues.fontFamily || 'Arial, sans-serif';
    
    const handleFontChange = (event) => {
      const selectedFont = event.target.value;
      const fontObj = googleFonts.find(f => f.value === selectedFont);
      
      if (fontObj && fontObj.googleFont) {
        loadGoogleFont(selectedFont);
      }
      
      handleQuickUpdate('fontFamily', selectedFont);
    };
    
    return (
      <Box sx={{ mb: 2 }}>
        <FormControl fullWidth>
          <InputLabel>Font Family</InputLabel>
          <Select
            value={currentFont}
            onChange={handleFontChange}
            label="Font Family"
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
            }}
          >
            {googleFonts.map((font, index) => (
              <MenuItem 
                key={index} 
                value={font.value}
                sx={{ 
                  fontFamily: font.value,
                  borderBottom: font.category !== googleFonts[index + 1]?.category ? '1px solid #eee' : 'none'
                }}
              >
                <Box>
                  <Typography sx={{ fontFamily: font.value, fontSize: '14px' }}>
                    {font.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#666', fontSize: '11px' }}>
                    {font.category}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  };

  const renderAlignmentControls = () => {
    // Показываем элементы выравнивания для всех блоков, которые поддерживают textAlign
    const supportsAlignment = selectedBlock && [
      'hero', 'columns', 'columns-3', 'container', 'contactUs'
    ].includes(selectedBlock.type);
    
    if (!supportsAlignment) return null;

    return (
      <Box sx={{ mb: 3 }}>
        {/* Горизонтальное выравнивание для всех поддерживаемых блоков */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {['columns', 'columns-3', 'container'].includes(selectedBlock.type) ? 'Horizontal Alignment' : 'Text Alignment'}
        </Typography>
        <ToggleButtonGroup
          value={localValues.textAlign !== undefined ? localValues.textAlign : (localValues.alignment !== undefined ? localValues.alignment : 'left')}
          exclusive
          onChange={(e, newAlignment) => {
            if (newAlignment !== null) {
              console.log('Text alignment changed to:', newAlignment);
              // Обновляем оба свойства для совместимости
              handleQuickUpdate('textAlign', newAlignment);
              if (['button', 'image'].includes(selectedBlock.type)) {
                handleQuickUpdate('alignment', newAlignment);
              }
            }
          }}
          sx={{ mb: 2, display: 'flex', width: '100%' }}
        >
          <ToggleButton value="left" sx={{ flex: 1 }}>
            <AlignHorizontalLeft />
          </ToggleButton>
          <ToggleButton value="center" sx={{ flex: 1 }}>
            <AlignHorizontalCenter />
          </ToggleButton>
          <ToggleButton value="right" sx={{ flex: 1 }}>
            <AlignHorizontalRight />
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Вертикальное выравнивание для колонок */}
        {(selectedBlock.type.includes('columns') || selectedBlock.type.includes('container')) && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Vertical Alignment
            </Typography>
            <ToggleButtonGroup
              value={localValues.verticalAlign || 'top'}
              exclusive
              onChange={(e, newAlignment) => {
                if (newAlignment !== null) {
                  handleQuickUpdate('verticalAlign', newAlignment);
                }
              }}
              sx={{ mb: 2, display: 'flex', width: '100%' }}
            >
              <ToggleButton value="top" sx={{ flex: 1 }}>
                <AlignVerticalTop />
              </ToggleButton>
              <ToggleButton value="center" sx={{ flex: 1 }}>
                <AlignVerticalCenter />
              </ToggleButton>
              <ToggleButton value="bottom" sx={{ flex: 1 }}>
                <AlignVerticalBottom />
              </ToggleButton>
            </ToggleButtonGroup>
          </>
        )}

        {/* Количество колонок для columns блоков */}
        {selectedBlock.type.includes('columns') && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Number of Columns
            </Typography>
            <ToggleButtonGroup
              value={localValues.columns || 2}
              exclusive
              onChange={(e, newColumns) => {
                if (newColumns !== null) {
                  handleQuickUpdate('columns', newColumns);
                }
              }}
              sx={{ mb: 2, display: 'flex', width: '100%' }}
            >
              <ToggleButton value={2} sx={{ flex: 1 }}>
                2 Cols
              </ToggleButton>
              <ToggleButton value={3} sx={{ flex: 1 }}>
                3 Cols
              </ToggleButton>
              <ToggleButton value={4} sx={{ flex: 1 }}>
                4 Cols
              </ToggleButton>
            </ToggleButtonGroup>
          </>
        )}

        {/* Отступы между колонками */}
        {selectedBlock.type.includes('columns') && (
          renderSlider('Column Gap', 'columnGap', 0, 50, 5, 'px')
        )}
      </Box>
    );
  };

  const renderLayoutProperties = () => {
    if (!selectedBlock || !['columns', 'columns-3', 'container'].includes(selectedBlock.type)) return null;

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, color: '#2c3e50' }}>
          Layout Styling
        </Typography>

        {/* Background Color */}
        {renderColorPicker('Background Color', 'backgroundColor')}
        
        {/* Border Radius for all layout blocks */}
        {renderSlider('Border Radius', 'borderRadius', 0, 50, 1, 'px')}

        {/* Container specific properties */}
        {selectedBlock.type === 'container' && (
          <>
            {/* Border */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Border
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
                size="small"
                label="Width"
                type="number"
                value={parseInt((localValues.border || '0px solid #ddd').split('px')[0]) || 0}
                onChange={(e) => {
                  const width = e.target.value;
                  const currentBorder = localValues.border || '0px solid #ddd';
                  const parts = currentBorder.split(' ');
                  const newBorder = `${width}px ${parts[1] || 'solid'} ${parts[2] || '#ddd'}`;
                  handleQuickUpdate('border', newBorder);
                }}
                sx={{ flex: 1 }}
                inputProps={{ min: 0, max: 10 }}
              />
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>Style</InputLabel>
            <Select
                  value={(localValues.border || '0px solid #ddd').split(' ')[1] || 'solid'}
                  onChange={(e) => {
                    const currentBorder = localValues.border || '0px solid #ddd';
                    const parts = currentBorder.split(' ');
                    const newBorder = `${parts[0] || '0px'} ${e.target.value} ${parts[2] || '#ddd'}`;
                    handleQuickUpdate('border', newBorder);
                  }}
                >
                  <MenuItem value="solid">Solid</MenuItem>
                  <MenuItem value="dashed">Dashed</MenuItem>
                  <MenuItem value="dotted">Dotted</MenuItem>
                  <MenuItem value="none">None</MenuItem>
            </Select>
          </FormControl>
            </Box>

            {/* Border Color */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Border Color
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
                  size="small"
                  value={(localValues.border || '0px solid #ddd').split(' ')[2] || '#ddd'}
                  onChange={(e) => {
                    const currentBorder = localValues.border || '0px solid #ddd';
                    const parts = currentBorder.split(' ');
                    const newBorder = `${parts[0] || '0px'} ${parts[1] || 'solid'} ${e.target.value}`;
                    handleQuickUpdate('border', newBorder, true);
                  }}
                  sx={{ flex: 1 }}
                />
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    backgroundColor: (localValues.border || '0px solid #ddd').split(' ')[2] || '#ddd',
                    border: '1px solid #ccc',
                    borderRadius: 1,
                    cursor: 'pointer',
                    position: 'relative'
                  }}
                >
                  <input
                    type="color"
                    value={(localValues.border || '0px solid #ddd').split(' ')[2] || '#ddd'}
                    onChange={(e) => {
                      const currentBorder = localValues.border || '0px solid #ddd';
                      const parts = currentBorder.split(' ');
                      const newBorder = `${parts[0] || '0px'} ${parts[1] || 'solid'} ${e.target.value}`;
                      handleQuickUpdate('border', newBorder, true);
                    }}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      opacity: 0,
                      cursor: 'pointer',
                      border: 'none',
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Max Width */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Max Width
            </Typography>
            <ToggleButtonGroup
              value={localValues.maxWidth === '100%' ? 'full' : 'custom'}
              exclusive
              onChange={(e, value) => {
                if (value === 'full') {
                  handleQuickUpdate('maxWidth', '100%');
                } else if (value === 'custom') {
                  handleQuickUpdate('maxWidth', '600px');
                }
              }}
              sx={{ mb: 2, display: 'flex', width: '100%' }}
            >
              <ToggleButton value="full" sx={{ flex: 1 }}>
                Full Width
              </ToggleButton>
              <ToggleButton value="custom" sx={{ flex: 1 }}>
                Custom
              </ToggleButton>
            </ToggleButtonGroup>

            {localValues.maxWidth !== '100%' && (
          <TextField
            fullWidth
                size="small"
                label="Custom Width"
                value={localValues.maxWidth || '600px'}
                onChange={(e) => handleQuickUpdate('maxWidth', e.target.value)}
            sx={{ mb: 2 }}
                placeholder="e.g., 600px, 80%, auto"
              />
            )}
          </>
        )}

        {/* Columns specific properties */}
        {selectedBlock.type.includes('columns') && (
          <>
            {/* Column Width Distribution */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Column Width Distribution
            </Typography>
            {selectedBlock.type === 'columns' && localValues.columns === 2 && (
              <Box sx={{ mb: 2 }}>
                <ToggleButtonGroup
                  value={
                    localValues.leftWidth === '50%' && localValues.rightWidth === '50%' ? 'equal' :
                    localValues.leftWidth === '33%' && localValues.rightWidth === '67%' ? '1-2' :
                    localValues.leftWidth === '67%' && localValues.rightWidth === '33%' ? '2-1' : 'custom'
                  }
                  exclusive
                  onChange={(e, value) => {
                    if (value === 'equal') {
                      handleQuickUpdate('leftWidth', '50%');
                      handleQuickUpdate('rightWidth', '50%');
                    } else if (value === '1-2') {
                      handleQuickUpdate('leftWidth', '33%');
                      handleQuickUpdate('rightWidth', '67%');
                    } else if (value === '2-1') {
                      handleQuickUpdate('leftWidth', '67%');
                      handleQuickUpdate('rightWidth', '33%');
                    }
                  }}
                  sx={{ display: 'flex', width: '100%' }}
                >
                  <ToggleButton value="equal" sx={{ flex: 1, fontSize: '10px' }}>
                    50/50
                  </ToggleButton>
                  <ToggleButton value="1-2" sx={{ flex: 1, fontSize: '10px' }}>
                    33/67
                  </ToggleButton>
                  <ToggleButton value="2-1" sx={{ flex: 1, fontSize: '10px' }}>
                    67/33
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}

            {/* Background Image */}
          <TextField
            fullWidth
              size="small"
              label="Background Image URL"
              value={localValues.backgroundImage || ''}
              onChange={(e) => handleQuickUpdate('backgroundImage', e.target.value)}
            sx={{ mb: 2 }}
              placeholder="https://example.com/image.jpg"
            />

            {/* Background Size */}
            {localValues.backgroundImage && (
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Background Size</InputLabel>
            <Select
                  value={localValues.backgroundSize || 'cover'}
                  onChange={(e) => handleQuickUpdate('backgroundSize', e.target.value)}
                >
                  <MenuItem value="cover">Cover</MenuItem>
                  <MenuItem value="contain">Contain</MenuItem>
                  <MenuItem value="auto">Auto</MenuItem>
                  <MenuItem value="100% 100%">Stretch</MenuItem>
            </Select>
          </FormControl>
            )}

            {/* Background Position */}
            {localValues.backgroundImage && (
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Background Position</InputLabel>
            <Select
                  value={localValues.backgroundPosition || 'center'}
                  onChange={(e) => handleQuickUpdate('backgroundPosition', e.target.value)}
                >
              <MenuItem value="center">Center</MenuItem>
                  <MenuItem value="top">Top</MenuItem>
                  <MenuItem value="bottom">Bottom</MenuItem>
                  <MenuItem value="left">Left</MenuItem>
              <MenuItem value="right">Right</MenuItem>
                  <MenuItem value="top left">Top Left</MenuItem>
                  <MenuItem value="top right">Top Right</MenuItem>
                  <MenuItem value="bottom left">Bottom Left</MenuItem>
                  <MenuItem value="bottom right">Bottom Right</MenuItem>
            </Select>
          </FormControl>
            )}
          </>
        )}

        {/* Opacity/Transparency */}
        {renderSlider('Opacity', 'opacity', 0, 1, 0.1, '')}

        {/* Shadow */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Box Shadow
          </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(localValues.boxShadow && localValues.boxShadow !== 'none')}
                onChange={(e) => {
                if (e.target.checked) {
                  handleQuickUpdate('boxShadow', '0 4px 6px rgba(0, 0, 0, 0.1)');
                } else {
                  handleQuickUpdate('boxShadow', 'none');
                }
              }}
            />
          }
          label="Enable Shadow"
          sx={{ mb: 2 }}
        />

        {localValues.boxShadow && localValues.boxShadow !== 'none' && (
          <Box sx={{ mb: 2 }}>
            <ToggleButtonGroup
              value={
                localValues.boxShadow === '0 2px 4px rgba(0, 0, 0, 0.1)' ? 'light' :
                localValues.boxShadow === '0 4px 6px rgba(0, 0, 0, 0.1)' ? 'medium' :
                localValues.boxShadow === '0 8px 16px rgba(0, 0, 0, 0.15)' ? 'heavy' : 'custom'
              }
              exclusive
              onChange={(e, value) => {
                if (value === 'light') {
                  handleQuickUpdate('boxShadow', '0 2px 4px rgba(0, 0, 0, 0.1)');
                } else if (value === 'medium') {
                  handleQuickUpdate('boxShadow', '0 4px 6px rgba(0, 0, 0, 0.1)');
                } else if (value === 'heavy') {
                  handleQuickUpdate('boxShadow', '0 8px 16px rgba(0, 0, 0, 0.15)');
                }
              }}
              sx={{ display: 'flex', width: '100%' }}
            >
              <ToggleButton value="light" sx={{ flex: 1, fontSize: '11px' }}>
                Light
              </ToggleButton>
              <ToggleButton value="medium" sx={{ flex: 1, fontSize: '11px' }}>
                Medium
              </ToggleButton>
              <ToggleButton value="heavy" sx={{ flex: 1, fontSize: '11px' }}>
                Heavy
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}
      </Box>
    );
  };

  

  const renderSpacingProperties = () => {
    if (!selectedBlock) return null;

    // Проверяем, можно ли редактировать блок
    const isBlockReadOnly = selectedBlock.type === 'unsubscribe' && !isAdminRole(user?.role);

    return (
      <Accordion 
        expanded={expandedSection === 'spacing'} 
        onChange={() => setExpandedSection(expandedSection === 'spacing' ? '' : 'spacing')}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Settings sx={{ mr: 1 }} />
          <Typography>Spacing & Layout</Typography>
          {isBlockReadOnly && (
            <Typography variant="caption" sx={{ ml: 1, color: 'orange' }}>
              (Read-only)
            </Typography>
          )}
        </AccordionSummary>
        <AccordionDetails>
          <TextField
            fullWidth
            type="number"
            label="Margin Top (px)"
            value={localValues.marginTop !== undefined ? localValues.marginTop : 0}
            onChange={(e) => handleQuickUpdate('marginTop', parseInt(e.target.value) || 0)}
            sx={{ mb: 2 }}
            inputProps={{ min: 0, max: 200 }}
            disabled={isBlockReadOnly}
          />

          <TextField
            fullWidth
            type="number"
            label="Margin Bottom (px)"
            value={localValues.marginBottom !== undefined ? localValues.marginBottom : 0}
            onChange={(e) => handleQuickUpdate('marginBottom', parseInt(e.target.value) || 0)}
            sx={{ mb: 2 }}
            inputProps={{ min: 0, max: 200 }}
            disabled={isBlockReadOnly}
          />

          <TextField
            fullWidth
            type="number"
            label="Padding (px)"
            value={localValues.padding !== undefined ? localValues.padding : 0}
            onChange={(e) => handleQuickUpdate('padding', parseInt(e.target.value) || 0)}
            sx={{ mb: 2 }}
            inputProps={{ min: 0, max: 200 }}
            disabled={isBlockReadOnly}
          />
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderTemplateSettings = () => (
    <Accordion 
      expanded={expandedSection === 'template'} 
      onChange={() => setExpandedSection(expandedSection === 'template' ? '' : 'template')}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Settings sx={{ mr: 1 }} />
        <Typography>Template Settings</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
            <span>Template Width</span>
            <span style={{ fontWeight: 'bold', color: '#007bff' }}>{templateSettings.width || 600}px</span>
          </Typography>
          <Slider
            value={templateSettings.width || 600}
            onChange={(e, newValue) => handleSettingsUpdate('width', newValue)}
            min={300}
            max={1600}
            step={10}
            size="small"
            sx={{
              color: '#007bff',
              '& .MuiSlider-thumb': {
                width: 16,
                height: 16,
                '&:hover, &.Mui-focusVisible': {
                  boxShadow: '0px 0px 0px 8px rgba(0, 123, 255, 0.16)',
                },
              },
            }}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Background Color
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <TextField
              size="small"
              value={templateSettings.backgroundColor || '#ffffff'}
              onChange={(e) => handleSettingsUpdate('backgroundColor', e.target.value)}
              placeholder="#ffffff"
              sx={{ flex: 1 }}
            />
            <Box
              sx={{
                width: 40,
                height: 32,
                backgroundColor: templateSettings.backgroundColor || '#ffffff',
                border: '1px solid #ddd',
                borderRadius: 1,
                cursor: 'pointer',
                position: 'relative',
                flexShrink: 0,
              }}
            >
              <input
                type="color"
                value={templateSettings.backgroundColor || '#ffffff'}
                onChange={(e) => handleSettingsUpdate('backgroundColor', e.target.value)}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  opacity: 0,
                  cursor: 'pointer',
                  border: 'none',
                }}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
            <span>Template Padding</span>
            <span style={{ fontWeight: 'bold', color: '#007bff' }}>{templateSettings.padding !== undefined ? templateSettings.padding : 20}px</span>
          </Typography>
          <Slider
            value={templateSettings.padding !== undefined ? templateSettings.padding : 20}
            onChange={(e, newValue) => handleSettingsUpdate('padding', newValue)}
            min={0}
            max={60}
            step={1}
            size="small"
            sx={{
              color: '#007bff',
              '& .MuiSlider-thumb': {
                width: 16,
                height: 16,
                '&:hover, &.Mui-focusVisible': {
                  boxShadow: '0px 0px 0px 8px rgba(0, 123, 255, 0.16)',
                },
              },
            }}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
            <span>Block Spacing</span>
            <span style={{ fontWeight: 'bold', color: '#007bff' }}>{templateSettings.blockSpacing !== undefined ? templateSettings.blockSpacing : 5}px</span>
          </Typography>
          <Slider
            value={templateSettings.blockSpacing !== undefined ? templateSettings.blockSpacing : 5}
            onChange={(e, newValue) => handleSettingsUpdate('blockSpacing', newValue)}
            min={0}
            max={40}
            step={1}
            size="small"
            sx={{
              color: '#007bff',
              '& .MuiSlider-thumb': {
                width: 16,
                height: 16,
                '&:hover, &.Mui-focusVisible': {
                  boxShadow: '0px 0px 0px 8px rgba(0, 123, 255, 0.16)',
                },
              },
            }}
          />
        </Box>
      </AccordionDetails>
    </Accordion>
  );

  // Универсальные свойства для всех блоков
  const renderBlockProperties = () => {
    if (!selectedBlock) return null;

    return (
      <Accordion 
        expanded={expandedSection === 'visual'} 
        onChange={() => setExpandedSection(expandedSection === 'visual' ? '' : 'visual')}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Settings sx={{ mr: 1 }} />
          <Typography>Block Properties</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {/* Цветовые свойства */}
          {renderBlockColors()}
          
          {/* Размеры и стили */}
          {renderBlockSizes()}
          
          {/* Выравнивание */}
          {renderAlignmentControls()}
          
          {/* Layout Properties */}
          {renderLayoutProperties()}
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderBlockColors = () => {
    const colors = [];
    
    // Цвета для разных типов блоков
    switch (selectedBlock.type) {
      case 'text':
      case 'heading':
        colors.push(['Text Color', 'color']);
        break;
      case 'button':
        colors.push(['Button Color', 'backgroundColor']);
        colors.push(['Text Color', 'color']);
        break;
      case 'image':
        colors.push(['Background Color', 'backgroundColor']);
        break;
      case 'divider':
        colors.push(['Divider Color', 'color']);
        break;
      case 'list':
        colors.push(['Text Color', 'color']);
        colors.push(['Background Color', 'backgroundColor']);
        break;
      case 'hero':
        colors.push(['Background Color', 'backgroundColor']);
        colors.push(['Title Color', 'titleColor']);
        colors.push(['Subtitle Color', 'subtitleColor']);
        colors.push(['Button Color', 'buttonBackgroundColor']);
        break;
      case 'testimonial':
        colors.push(['Background Color', 'backgroundColor']);
        colors.push(['Quote Color', 'quoteColor']);
        colors.push(['Author Color', 'authorColor']);
        colors.push(['Position Color', 'positionColor']);
        colors.push(['Company Color', 'companyColor']);
        break;
      case 'product':
        colors.push(['Background Color', 'backgroundColor']);
        colors.push(['Title Color', 'titleColor']);
        colors.push(['Description Color', 'descriptionColor']);
        colors.push(['Price Color', 'priceColor']);
        colors.push(['Button Background', 'buttonBackgroundColor']);
        break;
      case 'event':
        colors.push(['Background Color', 'backgroundColor']);
        colors.push(['Title Color', 'titleColor']);
        colors.push(['Description Color', 'descriptionColor']);
        colors.push(['Date & Time Color', 'dateColor']);
        colors.push(['Button Background', 'buttonBackgroundColor']);
        break;
      case 'footer':
        colors.push(['Background Color', 'backgroundColor']);
        colors.push(['Text Color', 'textColor']);
        break;
      case 'contactUs':
        colors.push(['Background Color', 'backgroundColor']);
        colors.push(['Title Color', 'titleColor']);
        colors.push(['Name Color', 'nameColor']);
        colors.push(['Position Color', 'positionColor']);
        colors.push(['Description Color', 'descriptionColor']);
        colors.push(['Contact Color', 'contactColor']);
        colors.push(['Border Color', 'borderColor']);
        break;
      case 'unsubscribe':
        colors.push(['Text Color', 'textColor']);
        colors.push(['Button Color', 'buttonColor']);
        colors.push(['Button Text Color', 'buttonTextColor']);
        break;
      case 'social-icons':
        // Цвета для social-icons блока только при style=monochrome
        if (localValues.style === 'monochrome') {
          colors.push(['Background Color', 'backgroundColor']);
          colors.push(['Icon Color', 'iconColor']);
        }
        break;
      default:
        break;
    }

    // Общий background color для блоков которые его поддерживают
    if (['spacer'].includes(selectedBlock.type)) {
      colors.push(['Background Color', 'backgroundColor']);
    }

    return dedupColors(colors).map(([label, property]) => renderColorPicker(label, property));
  };

  function dedupColors(colorPairs) {
    const seen = new Set();
    return colorPairs.filter(([_, prop]) => {
      if (seen.has(prop)) return false;
      seen.add(prop);
      return true;
    });
  }

  const renderBlockSizes = () => {
    const sizes = [];
    
    switch (selectedBlock.type) {
      case 'divider':
        sizes.push(renderSlider('Height', 'height', 1, 20, 1, 'px'));
        break;
      case 'spacer':
        sizes.push(renderSlider('Height', 'height', 10, 200, 5, 'px'));
        break;
      case 'button':
        sizes.push(renderSlider('Border Radius', 'borderRadius', 0, 50, 1, 'px'));
        sizes.push(renderSlider('Font Size', 'fontSize', 10, 24, 1, 'px'));
        break;
      case 'image':
        sizes.push(renderSlider('Border Radius', 'borderRadius', 0, 50, 1, 'px'));
        
        // Image Width - поддерживает как пиксели, так и проценты
        sizes.push(
          <Box key="image-width" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
              <span>Width</span>
              <span style={{ fontWeight: 'bold', color: '#007bff' }}>{localValues.width || '100%'}</span>
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Slider
                value={parseInt(localValues.width) || 100}
                onChange={(e, newValue) => handleQuickUpdate('width', `${newValue}%`)}
                min={10}
                max={100}
                step={5}
                size="small"
                sx={{
                  flex: 1,
                  color: '#007bff',
                  '& .MuiSlider-thumb': {
                    width: 16,
                    height: 16,
                  },
                }}
              />
              <TextField
                size="small"
                value={localValues.width || '100%'}
                onChange={(e) => handleQuickUpdate('width', e.target.value)}
                sx={{ width: '80px' }}
                placeholder="100%"
              />
            </Box>
          </Box>
        );

        // Image Alignment
        sizes.push(
          <Box key="image-alignment" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Image Alignment
            </Typography>
            <ToggleButtonGroup
              value={localValues.alignment || 'center'}
              exclusive
              onChange={(e, newAlign) => {
                if (newAlign !== null) {
                  handleQuickUpdate('alignment', newAlign);
                }
              }}
              sx={{ display: 'flex', width: '100%' }}
            >
              <ToggleButton value="left" sx={{ flex: 1 }}>
                <FormatAlignLeft />
              </ToggleButton>
              <ToggleButton value="center" sx={{ flex: 1 }}>
                <FormatAlignCenter />
              </ToggleButton>
              <ToggleButton value="right" sx={{ flex: 1 }}>
                <FormatAlignRight />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        );
        break;
      case 'text':
      case 'heading':
        sizes.push(renderSlider('Font Size', 'fontSize', 10, 48, 1, 'px'));
        sizes.push(renderSlider('Line Height', 'lineHeight', 1.0, 3.0, 0.1, ''));
        break;
      case 'list':
        sizes.push(renderSlider('Font Size', 'fontSize', 10, 24, 1, 'px'));
        break;

      case 'contactUs':
        sizes.push(renderSlider('Title Font Size', 'titleFontSize', 16, 48, 1, 'px'));
        sizes.push(renderSlider('Name Font Size', 'nameFontSize', 12, 24, 1, 'px'));
        sizes.push(renderSlider('Position Font Size', 'positionFontSize', 10, 20, 1, 'px'));
        sizes.push(renderSlider('Description Font Size', 'descriptionFontSize', 10, 20, 1, 'px'));
        sizes.push(renderSlider('Contact Font Size', 'contactFontSize', 10, 18, 1, 'px'));
        sizes.push(renderSlider('Card Spacing', 'cardSpacing', 0, 50, 5, 'px'));
        break;
      case 'social-icons':
        // Style selector
        sizes.push(
          <Box key="social-style" sx={{ mb: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Style</InputLabel>
              <Select
                value={localValues.style || 'colored'}
                onChange={(e) => handleQuickUpdate('style', e.target.value)}
              >
                <MenuItem value="colored">Colored</MenuItem>
                <MenuItem value="monochrome">Monochrome</MenuItem>
              </Select>
            </FormControl>
          </Box>
        );
        
        // Icon Size slider
        sizes.push(renderSlider('Icon Size', 'iconSize', 16, 64, 2, 'px'));
        
        // Border Radius slider для скругления краев
        sizes.push(renderSlider('Border Radius', 'borderRadius', 0, 50, 1, '%'));
        
        // Icon Spacing slider
        sizes.push(renderSlider('Icon Spacing', 'spacing', 0, 30, 1, 'px'));
        break;
      case 'unsubscribe':
        sizes.push(renderSlider('Font Size', 'fontSize', 8, 18, 1, 'px'));
        break;
      default:
        break;
    }

    return sizes;
  };

  // Содержимое блока
  const renderContentProperties = () => {
    if (!selectedBlock) return null;

    return (
      <Accordion 
        expanded={expandedSection === 'content'} 
        onChange={() => setExpandedSection(expandedSection === 'content' ? '' : 'content')}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Edit sx={{ mr: 1 }} />
          <Typography>Content</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {/* Текстовое содержимое */}
          {['text', 'heading'].includes(selectedBlock.type) && (
            <>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Content"
                value={localValues.content || localValues.text || ''}
                onChange={(e) => handleQuickUpdate('content', e.target.value)}
                sx={{ mb: 2 }}
              />
              
              {/* Text Formatting */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Text Formatting
              </Typography>
              
              {/* Font Family */}
              {renderFontFamilyControl()}
              
              {/* Font Weight */}
              {renderFontWeightControl()}
              
              {/* Text Style (Italic, Underline, Strikethrough) */}
              {renderTextStyleControls()}
              
              {/* Text Alignment */}
              {renderTextAlignmentControl()}
            </>
          )}

          {/* Кнопка */}
          {selectedBlock.type === 'button' && (
            <>
              <TextField
                fullWidth
                label="Button Text"
                value={localValues.text || ''}
                onChange={(e) => handleQuickUpdate('text', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Link URL"
                value={localValues.link || ''}
                onChange={(e) => handleQuickUpdate('link', e.target.value)}
                sx={{ mb: 2 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={localValues.fullWidth || false}
                    onChange={(e) => handleQuickUpdate('fullWidth', e.target.checked, true)}
                  />
                }
                label="Full Width"
                sx={{ mb: 2 }}
              />
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Button Alignment
                </Typography>
                <ToggleButtonGroup
                  value={localValues.alignment || 'center'}
                  exclusive
                  onChange={(e, newAlign) => {
                    if (newAlign !== null) {
                      handleQuickUpdate('alignment', newAlign);
                    }
                  }}
                  sx={{ display: 'flex', width: '100%' }}
                >
                  <ToggleButton value="left" sx={{ flex: 1 }}>
                    <FormatAlignLeft />
                  </ToggleButton>
                  <ToggleButton value="center" sx={{ flex: 1 }}>
                    <FormatAlignCenter />
                  </ToggleButton>
                  <ToggleButton value="right" sx={{ flex: 1 }}>
                    <FormatAlignRight />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              {/* Text Formatting */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Button Text Formatting
              </Typography>
              
              {/* Font Family */}
              {renderFontFamilyControl()}
              
              {/* Font Weight */}
              {renderFontWeightControl()}
            </>
          )}

          {/* Изображение */}
          {selectedBlock.type === 'image' && (
            <>
              <TextField
                fullWidth
                label="Image URL"
                value={localValues.src || ''}
                onChange={(e) => handleQuickUpdate('src', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Alt Text"
                value={localValues.alt || ''}
                onChange={(e) => handleQuickUpdate('alt', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Link URL"
                value={localValues.link || ''}
                onChange={(e) => handleQuickUpdate('link', e.target.value)}
                sx={{ mb: 2 }}
              />
            </>
          )}

          {/* Container блок */}
          {selectedBlock.type === 'container' && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Container Settings
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
                  <span>Padding</span>
                  <span style={{ fontWeight: 'bold', color: '#007bff' }}>{localValues.padding || 10}px</span>
                </Typography>
                <Slider
                  value={localValues.padding || 10}
                  onChange={(e, newValue) => handleQuickUpdate('padding', newValue)}
                  min={0}
                  max={80}
                  step={1}
                  size="small"
                  sx={{
                    color: '#007bff',
                    '& .MuiSlider-thumb': {
                      width: 16,
                      height: 16,
                    },
                  }}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
                  <span>Margin Top</span>
                  <span style={{ fontWeight: 'bold', color: '#007bff' }}>{localValues.marginTop || 15}px</span>
                </Typography>
                <Slider
                  value={localValues.marginTop || 15}
                  onChange={(e, newValue) => handleQuickUpdate('marginTop', newValue)}
                  min={0}
                  max={100}
                  step={1}
                  size="small"
                  sx={{
                    color: '#007bff',
                    '& .MuiSlider-thumb': {
                      width: 16,
                      height: 16,
                    },
                  }}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
                  <span>Margin Bottom</span>
                  <span style={{ fontWeight: 'bold', color: '#007bff' }}>{localValues.marginBottom || 15}px</span>
                </Typography>
                <Slider
                  value={localValues.marginBottom || 15}
                  onChange={(e, newValue) => handleQuickUpdate('marginBottom', newValue)}
                  min={0}
                  max={100}
                  step={1}
                  size="small"
                  sx={{
                    color: '#007bff',
                    '& .MuiSlider-thumb': {
                      width: 16,
                      height: 16,
                    },
                  }}
                />
              </Box>

            </>
          )}

          {/* Колонки */}
          {(selectedBlock.type === 'columns' || selectedBlock.type === 'columns-3') && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Column Settings
              </Typography>
              
              {selectedBlock.type === 'columns' && (
                <>
                  <TextField
                    fullWidth
                    label="Left Column Width (%)"
                    type="number"
                    value={parseInt(localValues.leftWidth) || 50}
                    onChange={(e) => handleQuickUpdate('leftWidth', `${e.target.value}%`)}
                    sx={{ mb: 2 }}
                    inputProps={{ min: 10, max: 90 }}
                  />
                  <TextField
                    fullWidth
                    label="Right Column Width (%)"
                    type="number"
                    value={parseInt(localValues.rightWidth) || 50}
                    onChange={(e) => handleQuickUpdate('rightWidth', `${e.target.value}%`)}
                    sx={{ mb: 2 }}
                    inputProps={{ min: 10, max: 90 }}
                  />
                </>
              )}
              
              {renderSlider('Column Gap', 'columnGap', 0, 50, 5, 'px')}
            </>
          )}

          {/* Список */}
          {selectedBlock.type === 'list' && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>List Type</InputLabel>
                <Select
                  value={localValues.listType || 'bullet'}
                  onChange={(e) => handleQuickUpdate('listType', e.target.value, true)}
                >
                  <MenuItem value="bullet">Bullet Points</MenuItem>
                  <MenuItem value="numbered">Numbered</MenuItem>
                  <MenuItem value="none">No Markers</MenuItem>
                </Select>
              </FormControl>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                List Items
              </Typography>
              
              {(localValues.items || ['Item 1', 'Item 2']).map((item, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    value={item}
                    onChange={(e) => {
                      const newItems = [...(localValues.items || [])];
                      newItems[index] = e.target.value;
                      handleQuickUpdate('items', newItems);
                    }}
                    sx={{ mr: 1 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => {
                      const newItems = (localValues.items || []).filter((_, i) => i !== index);
                      handleQuickUpdate('items', newItems);
                    }}
                  >
                    <Remove />
                  </IconButton>
                </Box>
              ))}

              <Button
                startIcon={<Add />}
                onClick={() => {
                  const newItems = [...(localValues.items || []), 'New item'];
                  handleQuickUpdate('items', newItems);
                }}
                sx={{ mb: 2 }}
              >
                Add Item
              </Button>
              
              <Divider sx={{ my: 2 }} />
              
              {/* Text Formatting */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                List Text Formatting
              </Typography>
              
              {/* Font Family */}
              {renderFontFamilyControl()}
            </>
          )}

          {/* Разделитель */}
          {selectedBlock.type === 'divider' && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Divider Style
                </Typography>
                <ToggleButtonGroup
                  value={localValues.style || 'solid'}
                  exclusive
                  onChange={(e, newStyle) => {
                    if (newStyle !== null) {
                      handleQuickUpdate('style', newStyle);
                    }
                  }}
                  sx={{ display: 'flex', width: '100%' }}
                >
                  <ToggleButton value="solid" sx={{ flex: 1, fontSize: '12px' }}>
                    Solid
                  </ToggleButton>
                  <ToggleButton value="dashed" sx={{ flex: 1, fontSize: '12px' }}>
                    Dashed
                  </ToggleButton>
                  <ToggleButton value="dotted" sx={{ flex: 1, fontSize: '12px' }}>
                    Dotted
                  </ToggleButton>
                  <ToggleButton value="double" sx={{ flex: 1, fontSize: '12px' }}>
                    Double
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              
              <TextField
                fullWidth
                label="Width (%)"
                type="number"
                value={parseInt(localValues.width) || 100}
                onChange={(e) => handleQuickUpdate('width', `${e.target.value}%`)}
                sx={{ mb: 2 }}
                inputProps={{ min: 10, max: 100 }}
              />
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Divider Alignment
                </Typography>
                <ToggleButtonGroup
                  value={localValues.alignment || 'center'}
                  exclusive
                  onChange={(e, newAlign) => {
                    if (newAlign !== null) {
                      handleQuickUpdate('alignment', newAlign);
                    }
                  }}
                  sx={{ display: 'flex', width: '100%' }}
                >
                  <ToggleButton value="left" sx={{ flex: 1 }}>
                    <FormatAlignLeft />
                  </ToggleButton>
                  <ToggleButton value="center" sx={{ flex: 1 }}>
                    <FormatAlignCenter />
                  </ToggleButton>
                  <ToggleButton value="right" sx={{ flex: 1 }}>
                    <FormatAlignRight />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </>
          )}

          {/* Спейсер - только контентные настройки */}
          {selectedBlock.type === 'spacer' && (
            <Typography variant="body2" color="text.secondary">
              Spacer block has no content settings. Use Block Properties to adjust height.
            </Typography>
          )}

          {/* Social Icons блок */}
          {selectedBlock.type === 'social-icons' && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Social Media & Messengers
              </Typography>
              
              {/* Category Selector */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Add New Platform
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel size="small">Category</InputLabel>
                    <Select
                      size="small"
                      defaultValue=""
                      onChange={(e) => {
                        const category = e.target.value;
                        const newPlatform = { 
                          category, 
                          name: category === 'social' ? 'Facebook' : 'Telegram', 
                          url: '', 
                          show: true 
                        };
                        const newPlatforms = [...(localValues.platforms || []), newPlatform];
                        handleQuickUpdate('platforms', newPlatforms);
                      }}
                    >
                      <MenuItem value="">Choose...</MenuItem>
                      <MenuItem value="social">Social Media</MenuItem>
                      <MenuItem value="messenger">Messengers</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {/* Existing Platforms */}
              {(localValues.platforms || []).map((platform, index) => (
                <Box key={index} sx={{ 
                  mb: 1, 
                  p: 1.5, 
                  border: '1px solid #e0e0e0', 
                  borderRadius: 1,
                  backgroundColor: platform.category === 'social' ? '#f8f9ff' : '#fff8f0'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* Platform Icon */}
                    <Box sx={{ 
                      width: 24, 
                      height: 24, 
                      borderRadius: '50%', 
                      backgroundColor: platform.category === 'social' ? '#1976d2' : '#ff9800',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      color: 'white',
                      fontWeight: 'bold'
                    }}>
                      {platform.category === 'social' ? 'S' : 'M'}
                    </Box>
                    
                    {/* Platform Name */}
                    <FormControl sx={{ minWidth: 120 }}>
                      <Select
                        size="small"
                        value={platform.name || 'Facebook'}
                        onChange={(e) => {
                          const newPlatforms = [...(localValues.platforms || [])];
                          newPlatforms[index] = { ...platform, name: e.target.value };
                          handleQuickUpdate('platforms', newPlatforms);
                        }}
                      >
                        {platform.category === 'social' ? [
                          'Facebook', 'Instagram', 'Odnoklassniki', 'LinkedIn', 'Tik-Tok', 'Twitter', 
                          'Twitter-X', 'Pinterest', 'YouTube', 'GitHub', 'Itunes'
                        ].map(name => (
                          <MenuItem key={name} value={name}>{name}</MenuItem>
                        )) : [
                          'Telegram', 'Vkontakte', 'Messenger Facebook', 'Messenger Line', 
                          'Whatsapp', 'Viber', 'Discord'
                        ].map(name => (
                          <MenuItem key={name} value={name}>{name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    
                    {/* URL Input */}
                    <TextField
                      size="small"
                      label="URL"
                      value={platform.url || ''}
                      onChange={(e) => {
                        const newPlatforms = [...(localValues.platforms || [])];
                        newPlatforms[index] = { ...platform, url: e.target.value };
                        handleQuickUpdate('platforms', newPlatforms);
                      }}
                      sx={{ flex: 1 }}
                    />
                    
                    {/* Remove Button */}
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        const newPlatforms = (localValues.platforms || []).filter((_, i) => i !== index);
                        handleQuickUpdate('platforms', newPlatforms);
                      }}
                    >
                      <Remove />
                    </IconButton>
                  </Box>
                </Box>
              ))}

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Social Icons Alignment
                </Typography>
                <ToggleButtonGroup
                  value={localValues.alignment || 'center'}
                  exclusive
                  onChange={(e, newAlign) => {
                    if (newAlign !== null) {
                      handleQuickUpdate('alignment', newAlign);
                    }
                  }}
                  sx={{ display: 'flex', width: '100%' }}
                >
                  <ToggleButton value="left" sx={{ flex: 1 }}>
                    <FormatAlignLeft />
                  </ToggleButton>
                  <ToggleButton value="center" sx={{ flex: 1 }}>
                    <FormatAlignCenter />
                  </ToggleButton>
                  <ToggleButton value="right" sx={{ flex: 1 }}>
                    <FormatAlignRight />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </>
          )}

          {/* Testimonial блок */}
          {selectedBlock.type === 'testimonial' && (
            <>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Quote"
                value={localValues.quote || ''}
                onChange={(e) => handleQuickUpdate('quote', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Author Name"
                value={localValues.author || ''}
                onChange={(e) => handleQuickUpdate('author', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Position"
                value={localValues.position || ''}
                onChange={(e) => handleQuickUpdate('position', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Company Name"
                value={localValues.companyName || ''}
                onChange={(e) => handleQuickUpdate('companyName', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Author Image URL"
                value={localValues.authorImage || ''}
                onChange={(e) => handleQuickUpdate('authorImage', e.target.value)}
                sx={{ mb: 2 }}
              />
              
              <Divider sx={{ my: 2 }} />
              
              {/* Text Formatting */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Text Formatting
              </Typography>
              
              {/* Font Family */}
              {renderFontFamilyControl()}
              
              {/* Text Alignment for Testimonial */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Text Alignment
                </Typography>
                <ToggleButtonGroup
                  value={localValues.alignment || 'center'}
                  exclusive
                  onChange={(e, newAlign) => {
                    if (newAlign !== null) {
                      console.log('Testimonial alignment changed to:', newAlign);
                      handleQuickUpdate('alignment', newAlign);
                    }
                  }}
                  sx={{ display: 'flex', width: '100%' }}
                >
                  <ToggleButton value="left" sx={{ flex: 1 }}>
                    <FormatAlignLeft />
                  </ToggleButton>
                  <ToggleButton value="center" sx={{ flex: 1 }}>
                    <FormatAlignCenter />
                  </ToggleButton>
                  <ToggleButton value="right" sx={{ flex: 1 }}>
                    <FormatAlignRight />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </>
          )}

          {/* Footer блок */}
          {selectedBlock.type === 'footer' && (
            <>
              <TextField
                fullWidth
                label="Company Name"
                value={localValues.companyName || ''}
                onChange={(e) => handleQuickUpdate('companyName', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Address"
                value={localValues.address || ''}
                onChange={(e) => handleQuickUpdate('address', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Phone"
                value={localValues.phone || ''}
                onChange={(e) => handleQuickUpdate('phone', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Email"
                value={localValues.email || ''}
                onChange={(e) => handleQuickUpdate('email', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Website"
                value={localValues.website || ''}
                onChange={(e) => handleQuickUpdate('website', e.target.value)}
                sx={{ mb: 2 }}
              />
              
              <Divider sx={{ my: 2 }} />
              
              {/* Text Formatting */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Text Formatting
              </Typography>
              
              {/* Font Family */}
              {renderFontFamilyControl()}
              
              {/* Text Alignment for Footer */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Text Alignment
                </Typography>
                <ToggleButtonGroup
                  value={localValues.alignment || 'center'}
                  exclusive
                  onChange={(e, newAlign) => {
                    if (newAlign !== null) {
                      console.log('Footer alignment changed to:', newAlign);
                      handleQuickUpdate('alignment', newAlign);
                    }
                  }}
                  sx={{ display: 'flex', width: '100%' }}
                >
                  <ToggleButton value="left" sx={{ flex: 1 }}>
                    <FormatAlignLeft />
                  </ToggleButton>
                  <ToggleButton value="center" sx={{ flex: 1 }}>
                    <FormatAlignCenter />
                  </ToggleButton>
                  <ToggleButton value="right" sx={{ flex: 1 }}>
                    <FormatAlignRight />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </>
          )}

          {/* Hero блок */}
          {selectedBlock.type === 'hero' && (
            <>
              <TextField
                fullWidth
                label="Title"
                value={localValues.title || ''}
                onChange={(e) => handleQuickUpdate('title', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={localValues.subtitle || localValues.description || ''}
                onChange={(e) => handleQuickUpdate('subtitle', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Button Text"
                value={localValues.buttonText || ''}
                onChange={(e) => handleQuickUpdate('buttonText', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Button Link"
                value={localValues.buttonLink || ''}
                onChange={(e) => handleQuickUpdate('buttonLink', e.target.value)}
                sx={{ mb: 2 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={localValues.showButton !== false}
                    onChange={(e) => handleQuickUpdate('showButton', e.target.checked, true)}
                  />
                }
                label="Show Button"
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Background Image URL"
                value={localValues.backgroundImage || ''}
                onChange={(e) => handleQuickUpdate('backgroundImage', e.target.value)}
                sx={{ mb: 2 }}
              />
              
              <Divider sx={{ my: 2 }} />
              
              {/* Text Formatting */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Text Formatting
              </Typography>
              
              {/* Font Family */}
              {renderFontFamilyControl()}
              
              {/* Text Alignment */}
              {renderTextAlignmentControl()}
            </>
          )}

          {/* Product блок */}
          {selectedBlock.type === 'product' && (
            <>
              <TextField
                fullWidth
                label="Product Name"
                value={localValues.name || ''}
                onChange={(e) => handleQuickUpdate('name', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={localValues.description || ''}
                onChange={(e) => handleQuickUpdate('description', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Price"
                value={localValues.price || ''}
                onChange={(e) => handleQuickUpdate('price', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Original Price (optional)"
                value={localValues.originalPrice || ''}
                onChange={(e) => handleQuickUpdate('originalPrice', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Image URL"
                value={localValues.image || ''}
                onChange={(e) => handleQuickUpdate('image', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Button Text"
                value={localValues.buttonText || ''}
                onChange={(e) => handleQuickUpdate('buttonText', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Button Link"
                value={localValues.buttonLink || ''}
                onChange={(e) => handleQuickUpdate('buttonLink', e.target.value)}
                sx={{ mb: 2 }}
              />
              
              <Divider sx={{ my: 2 }} />
              
              {/* Text Formatting */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Text Formatting
              </Typography>
              
              {/* Font Family */}
              {renderFontFamilyControl()}
            </>
          )}

          {/* Event блок */}
          {selectedBlock.type === 'event' && (
            <>
              <TextField
                fullWidth
                label="Event Title"
                value={localValues.title || ''}
                onChange={(e) => handleQuickUpdate('title', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={localValues.description || ''}
                onChange={(e) => handleQuickUpdate('description', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                type="date"
                label="Event Date"
                value={localValues.date || new Date().toISOString().split('T')[0]}
                onChange={(e) => handleQuickUpdate('date', e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                type="time"
                label="Event Time"
                value={localValues.time || '19:00'}
                onChange={(e) => handleQuickUpdate('time', e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Location"
                value={localValues.location || ''}
                onChange={(e) => handleQuickUpdate('location', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Button Text"
                value={localValues.buttonText || ''}
                onChange={(e) => handleQuickUpdate('buttonText', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Button Link"
                value={localValues.buttonLink || ''}
                onChange={(e) => handleQuickUpdate('buttonLink', e.target.value)}
                sx={{ mb: 2 }}
              />
              
              <Divider sx={{ my: 2 }} />
              
              {/* Text Formatting */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Text Formatting
              </Typography>
              
              {/* Font Family */}
              {renderFontFamilyControl()}
            </>
          )}

          {/* ContactUs блок */}
          {selectedBlock.type === 'contactUs' && (
            <>
              <TextField
                fullWidth
                label="Section Title"
                value={localValues.title || ''}
                onChange={(e) => handleQuickUpdate('title', e.target.value)}
                sx={{ mb: 2 }}
              />
              
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Contact Cards
              </Typography>
              
              {(localValues.contacts || []).map((contact, index) => (
                <Box key={contact.id || index} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2">Contact {index + 1}</Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        const newContacts = (localValues.contacts || []).filter((_, i) => i !== index);
                        handleQuickUpdate('contacts', newContacts);
                      }}
                    >
                      <Remove />
                    </IconButton>
                  </Box>
                  
                  <TextField
                    fullWidth
                    size="small"
                    label="Full Name"
                    value={contact.fullName || ''}
                    onChange={(e) => {
                      const newContacts = [...(localValues.contacts || [])];
                      newContacts[index] = { ...contact, fullName: e.target.value };
                      handleQuickUpdate('contacts', newContacts);
                    }}
                    sx={{ mb: 1 }}
                  />
                  
                  <TextField
                    fullWidth
                    size="small"
                    label="Position"
                    value={contact.position || ''}
                    onChange={(e) => {
                      const newContacts = [...(localValues.contacts || [])];
                      newContacts[index] = { ...contact, position: e.target.value };
                      handleQuickUpdate('contacts', newContacts);
                    }}
                    sx={{ mb: 1 }}
                  />
                  
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    label="Description"
                    value={contact.description || ''}
                    onChange={(e) => {
                      const newContacts = [...(localValues.contacts || [])];
                      newContacts[index] = { ...contact, description: e.target.value };
                      handleQuickUpdate('contacts', newContacts);
                    }}
                    sx={{ mb: 1 }}
                  />
                  
                  <TextField
                    fullWidth
                    size="small"
                    label="Phone Number"
                    value={contact.phone || ''}
                    onChange={(e) => {
                      const newContacts = [...(localValues.contacts || [])];
                      newContacts[index] = { ...contact, phone: e.target.value };
                      handleQuickUpdate('contacts', newContacts);
                    }}
                    sx={{ mb: 1 }}
                  />
                  
                  <TextField
                    fullWidth
                    size="small"
                    label="Email"
                    value={contact.email || ''}
                    onChange={(e) => {
                      const newContacts = [...(localValues.contacts || [])];
                      newContacts[index] = { ...contact, email: e.target.value };
                      handleQuickUpdate('contacts', newContacts);
                    }}
                    sx={{ mb: 1 }}
                  />
                  
                  <TextField
                    fullWidth
                    size="small"
                    label="Avatar URL"
                    value={contact.avatar || ''}
                    onChange={(e) => {
                      const newContacts = [...(localValues.contacts || [])];
                      newContacts[index] = { ...contact, avatar: e.target.value };
                      handleQuickUpdate('contacts', newContacts);
                    }}
                    placeholder="https://example.com/avatar.jpg"
                  />
                </Box>
              ))}
              
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Add />}
                onClick={() => {
                  const newContact = {
                    id: Date.now().toString(),
                    avatar: '',
                    fullName: 'New Contact',
                    position: 'Position',
                    description: 'Contact description...',
                    phone: '+1 (555) 000-0000',
                    email: 'contact@company.com'
                  };
                  const newContacts = [...(localValues.contacts || []), newContact];
                  handleQuickUpdate('contacts', newContacts);
                }}
                sx={{ mb: 2 }}
              >
                Add Contact
              </Button>
              
              {/* Layout Settings */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Layout Settings
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Cards Per Row</InputLabel>
                <Select
                  value={localValues.cardsPerRow || 2}
                  onChange={(e) => handleQuickUpdate('cardsPerRow', e.target.value)}
                >
                  <MenuItem value={1}>1 Card</MenuItem>
                  <MenuItem value={2}>2 Cards</MenuItem>
                  <MenuItem value={3}>3 Cards</MenuItem>
                  <MenuItem value={4}>4 Cards</MenuItem>
                </Select>
              </FormControl>
              
              {/* Text Alignment for ContactUs */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Text Alignment
                </Typography>
                <ToggleButtonGroup
                  value={localValues.alignment || 'center'}
                  exclusive
                  onChange={(e, newAlign) => {
                    if (newAlign !== null) {
                      console.log('ContactUs alignment changed to:', newAlign);
                      handleQuickUpdate('alignment', newAlign);
                    }
                  }}
                  sx={{ display: 'flex', width: '100%' }}
                >
                  <ToggleButton value="left" sx={{ flex: 1 }}>
                    <FormatAlignLeft />
                  </ToggleButton>
                  <ToggleButton value="center" sx={{ flex: 1 }}>
                    <FormatAlignCenter />
                  </ToggleButton>
                  <ToggleButton value="right" sx={{ flex: 1 }}>
                    <FormatAlignRight />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </>
          )}

          {/* Unsubscribe блок */}
          {selectedBlock.type === 'unsubscribe' && (
            <>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Unsubscribe Text"
                value={localValues.text || ''}
                onChange={(e) => handleQuickUpdate('text', e.target.value)}
                sx={{ mb: 2 }}
                disabled={!isAdminRole(user?.role)}
              />
              <TextField
                fullWidth
                label="Button Text"
                value={localValues.buttonText || ''}
                onChange={(e) => handleQuickUpdate('buttonText', e.target.value)}
                sx={{ mb: 2 }}
                disabled={!isAdminRole(user?.role)}
              />
              {!isAdminRole(user?.role) && (
                <Typography variant="caption" color="text.secondary">
                  Unsubscribe block is protected. Only administrators can edit it.
                </Typography>
              )}
            </>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Заголовок */}
      <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
        <Typography variant="h6" sx={{ color: '#2c3e50', fontWeight: 'bold' }}>
          Properties
        </Typography>
        {selectedBlock && (
          <Chip
            size="small"
            label={selectedBlock.type}
            sx={{ mt: 1, textTransform: 'capitalize' }}
          />
        )}
      </Box>

      {/* Панель свойств */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {selectedBlock ? (
          <Box>
            {renderContentProperties()}
            {renderBlockProperties()}
            {renderSpacingProperties()}
            <Divider sx={{ my: 1 }} />
            {renderTemplateSettings()}
          </Box>
        ) : (
          <Box sx={{ p: 3, textAlign: 'center', color: '#666' }}>
            <Visibility sx={{ fontSize: 48, mb: 2, opacity: 0.3 }} />
            <Typography variant="body2" sx={{ mb: 2 }}>
              Select a block to edit its properties
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Click on any block in the canvas to start editing
            </Typography>
            <Divider sx={{ my: 2 }} />
            {renderTemplateSettings()}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PropertyPanel; 